import { ThemedStyledInterface } from 'styled-components'
import * as styledComponents from 'styled-components'
import { ThemedStyledComponentsModule } from 'styled-components'
import { Theme } from './Theme'
import breakpoint from 'styled-components-breakpoint'
import DOMElements from './domElements'

const {
  default: _styled,
  css,
  createGlobalStyle,
  keyframes,
  ThemeProvider,
  withTheme
} = styledComponents as ThemedStyledComponentsModule<typeof Theme>

/**
 * ** Super ugly styled-components namespacing hack **
 *
 * This is probably the ugliest hack of the year 2019.
 * JS-in-CSS is super fancy, but has its downsides, especially in our "special" case where the application should be
 * included in another site.
 *
 * Inside our application, we want the standard CSS to be reset (see rules in ResetCSS.ts), but we don't want these
 * rules to apply outside our application. So we wrapped them in the `.implacheckCalculator` selector.
 *
 * The problem is that styled-components specifies its rules with lower specificity (e.g. `.sc-1234`), but
 * instead we need at least the specificity of the ResetCSS (so more something like `.implacheckCalculator .sc-1234`),
 * otherwise the ResetCSS styles cannot be overriden.
 */

/**
 * See the code after this one first, then this makes more sense.
 *
 * Since the components that are wrapped in the styled() call, (e.g. `styled(MyStyledComponent)`) are already monkey-
 * patched with the code seen below, we need to find the rule that closes the wrapper (`}`) and move it to the end of
 * the list, so that the newly added rules are also wrapped in the selector.
 */
const styled: ThemedStyledInterface<typeof Theme> = ((component) => {
  return (...args) => {
    const result = _styled(component)(...args)

    const openingNamespaceRule = (props) => props.className !== 'implacheckCalculator' && '.implacheckCalculator & {'
    const closingNamespaceRule = (props) => props.className !== 'implacheckCalculator' && '}'
    closingNamespaceRule.isClosingNamespace = true

    const reorderedRules = []
    let closingBracketRule = null
    result.componentStyle.rules.forEach(rule => {
      if (typeof rule === 'function' && rule.isClosingNamespace) {
        closingBracketRule = rule
        return
      }
      reorderedRules.push(rule)
    })
    if (closingBracketRule) {
      reorderedRules.push(closingBracketRule)
    } else {
      reorderedRules.unshift(openingNamespaceRule)
      reorderedRules.push(closingNamespaceRule)
    }

    result.componentStyle.rules = reorderedRules

    return result
  }
}) as any

/**
 * For every existing DOM element type, we create our own wrapper that calls the standard styled-components
 * function, but adds two more rules: One new rule before all existing rules (`.implacheckCalculator & {`) and
 * one after all rules (`}`) which closes the first rule.
 *
 * So we essentially want to wrap the resulting CSS in `.implacheckCalculator & { }` to have higher specificity.
 */
DOMElements.forEach(el => {
  styled[el] = (...args) => {
    const result = _styled[el](...args)

    const openingNamespaceRule = (props) => props.className !== 'implacheckCalculator' && '.implacheckCalculator & {'
    const closingNamespaceRule = (props) => props.className !== 'implacheckCalculator' && '}'
    closingNamespaceRule.isClosingNamespace = true

    result.componentStyle.rules = [
      openingNamespaceRule,
      ...result.componentStyle.rules,
      closingNamespaceRule
    ]
    return result
  }
})

export { css, createGlobalStyle, keyframes, ThemeProvider, withTheme, styled, breakpoint }
